/* iter-email-ops — Admin card for email deliverability ops.
 *
 * Surfaces three things on one panel:
 *   1. Rolling success/failure counts + %  over last 24 h (from
 *      `email_send_log`) so a domain-verification regression is
 *      visible without opening the log table.
 *   2. Latest Resend rate-limit + monthly-quota headers so a broadcast
 *      cron can't silently burn through the free-tier ceiling.
 *   3. "Clear failed" one-click purge — wipes only status=failed rows
 *      so historical successes stay intact for compliance / audit.
 *   4. Copy-pasteable DNS records for the send-subdomain migration
 *      (`send.<domain>`) so the reputation of admin@ inbound stays
 *      isolated from outbound blasts.
 */
import React, { useEffect, useState } from 'react';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  RefreshCw, Trash2, Copy as CopyIcon, Mail, Gauge, Globe2, Loader2,
  CheckCircle2, AlertTriangle, XCircle,
} from 'lucide-react';

function StatChip({ tone, children }) {
  const cls = tone === 'green'
    ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
    : tone === 'amber'
    ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400'
    : 'bg-rose-500/15 text-rose-700 dark:text-rose-400';
  return (
    <span className={`rounded-full px-2 py-[1px] text-[10px] font-semibold uppercase tracking-wider ${cls}`}>
      {children}
    </span>
  );
}

function copyToClipboard(v, label) {
  try {
    navigator.clipboard.writeText(v);
    toast.success(`${label} copied.`);
  } catch { toast.error('Copy failed — select and copy manually.'); }
}

export default function EmailOpsCard() {
  const [stats,   setStats]   = useState(null);
  const [dns,     setDns]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [purging, setPurging] = useState(false);

  const reload = () => {
    setLoading(true);
    Promise.all([
      adminApi.emailQuotaStats(24),
      adminApi.emailSubdomainDns(),
    ])
      .then(([s, d]) => { setStats(s); setDns(d); })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Load failed'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { reload(); }, []);

  const purgeFailed = async () => {
    const n = stats?.failed || 0;
    if (!n) { toast.info('No failed rows to purge.'); return; }
    if (!window.confirm(`Delete ${n} failed send-log entries? Sent rows are preserved.`)) return;
    setPurging(true);
    try {
      const r = await adminApi.emailPurgeFailed();
      toast.success(`Cleared ${r.deleted} failed row${r.deleted === 1 ? '' : 's'}.`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Purge failed');
    } finally { setPurging(false); }
  };

  if (loading || !stats) {
    return (
      <div className="mt-4 rounded-md border border-border bg-card p-4"
           data-testid="admin-email-ops-card">
        <div className="flex items-center gap-2 text-[12.5px] text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading email deliverability metrics…
        </div>
      </div>
    );
  }

  const sr = stats.success_rate_pct;
  const srTone = sr == null ? 'amber' : sr >= 99 ? 'green' : sr >= 90 ? 'amber' : 'red';
  const SrIcon = sr == null ? AlertTriangle : sr >= 99 ? CheckCircle2 : sr >= 90 ? AlertTriangle : XCircle;

  const rl        = stats.resend?.ratelimit_remaining;
  const rlLimit   = stats.resend?.ratelimit_limit;
  const monthly   = stats.resend?.monthly_quota;
  const rlTone    = rl == null ? 'amber'
                  : (rlLimit && rl < Math.max(1, rlLimit * 0.2)) ? 'amber'
                  : 'green';
  const monthlyTone = monthly == null ? 'amber'
                    : monthly <= 5 ? 'red'
                    : monthly <= 20 ? 'amber'
                    : 'green';

  return (
    <div className="mt-4 rounded-md border border-border bg-card p-4"
         data-testid="admin-email-ops-card">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-1.5 text-[12.5px] font-semibold">
            <Mail className="h-3.5 w-3.5 text-primary" /> Email Deliverability Ops
          </div>
          <div className="text-[11px] text-muted-foreground">
            Rolling 24 h success rate, latest Resend rate-limit headers,
            and the exact DNS records for the outbound subdomain migration.
          </div>
        </div>
        <Button size="sm" variant="ghost" onClick={reload}
                className="h-7 gap-1 text-muted-foreground"
                data-testid="admin-email-ops-refresh">
          <RefreshCw className="h-3 w-3" /> Refresh
        </Button>
      </div>

      {/* Row 1 — 24 h success rate + purge button */}
      <div className="mt-3 rounded border border-border bg-background p-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <div className="flex items-center gap-1.5 text-[12px] font-semibold">
              <SrIcon className={`h-3.5 w-3.5 ${
                srTone === 'green' ? 'text-emerald-600'
                : srTone === 'amber' ? 'text-amber-600'
                : 'text-rose-600'
              }`} />
              Last {stats.window_hours} h send success
              <StatChip tone={srTone}>
                {sr == null ? 'no data' : `${sr}%`}
              </StatChip>
            </div>
            <div className="mt-0.5 text-[10.5px] text-muted-foreground">
              <span data-testid="admin-email-ops-sent">{stats.sent} sent</span>
              {' · '}
              <span data-testid="admin-email-ops-failed">{stats.failed} failed</span>
              {' · '}
              <span>{stats.total} total</span>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={purgeFailed}
                  disabled={purging || stats.failed === 0}
                  className="h-8 gap-1.5"
                  data-testid="admin-email-ops-purge-failed">
            {purging ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
            Clear failed
          </Button>
        </div>
      </div>

      {/* Row 2 — Rate-limit + monthly quota */}
      <div className="mt-3 rounded border border-border bg-background p-3">
        <div className="flex items-center gap-1.5 text-[12px] font-semibold">
          <Gauge className="h-3.5 w-3.5 text-primary" /> Resend budget
          <StatChip tone={rlTone}>
            {rl == null ? 'unknown' : `${rl}/${rlLimit ?? '?'} /s`}
          </StatChip>
          <StatChip tone={monthlyTone}>
            {monthly == null ? 'unknown' : `${monthly} monthly left`}
          </StatChip>
        </div>
        <div className="mt-0.5 text-[10.5px] text-muted-foreground">
          {stats.resend?.checked_at
            ? `Checked ${new Date(stats.resend.checked_at).toLocaleString()} — headers captured on the last successful send.`
            : 'No headers captured yet. Send a test email to populate.'}
          {monthly != null && monthly <= 20 && (
            <> · <strong>Upgrade the Resend plan</strong> before a broadcast cron burns the remainder.</>
          )}
        </div>
      </div>

      {/* Row 3 — Send-subdomain DNS checklist */}
      {dns && (
        <div className="mt-3 rounded border border-border bg-background p-3"
             data-testid="admin-email-ops-subdomain">
          <div className="flex items-center gap-1.5 text-[12px] font-semibold">
            <Globe2 className="h-3.5 w-3.5 text-primary" /> Outbound subdomain
            <code className="rounded bg-muted px-1.5 py-0.5 font-mono-stat text-[10.5px]">
              {dns.target_domain}
            </code>
            {dns.configured
              ? <StatChip tone="green">CONFIGURED</StatChip>
              : <StatChip tone="amber">NOT SET</StatChip>}
          </div>
          <div className="mt-0.5 text-[10.5px] text-muted-foreground">
            {dns.configured
              ? <>Environment var <code>EMAIL_SEND_SUBDOMAIN</code> is set — every outbound send now uses this subdomain.</>
              : <>Isolate outbound onto <code>{dns.target_domain}</code> so a spam-flagged blast can't wreck the reputation of your inbound <code>admin@{dns.parent_domain}</code> mailbox.</>}
          </div>
          <div className="mt-2 space-y-1.5">
            {dns.records.map((r, i) => (
              <div key={i} className="rounded border border-border bg-muted/30 p-2 text-[10.5px]">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <span className="rounded-sm bg-primary/15 px-1 py-[1px] font-semibold uppercase tracking-wider text-primary">{r.type}</span>
                    <code className="ml-1.5 font-mono-stat">{r.host}</code>
                  </div>
                  <Button size="sm" variant="ghost"
                          className="h-6 gap-1 text-[10.5px] text-muted-foreground"
                          onClick={() => copyToClipboard(r.value, r.type + ' value')}
                          data-testid={`admin-email-ops-copy-${r.type.toLowerCase()}`}>
                    <CopyIcon className="h-3 w-3" /> Copy value
                  </Button>
                </div>
                <div className="mt-0.5 truncate font-mono-stat text-muted-foreground">{r.value}</div>
                <div className="text-[10px] italic text-muted-foreground">{r.purpose}</div>
              </div>
            ))}
          </div>
          <details className="mt-2 rounded border border-dashed border-border p-2 text-[10.5px]">
            <summary className="cursor-pointer text-muted-foreground">Setup checklist</summary>
            <ol className="mt-1.5 list-decimal space-y-0.5 pl-4 text-muted-foreground">
              {dns.checklist.map((step, i) => (
                <li key={i}>{step.replace(/^\d+\.\s*/, '')}</li>
              ))}
            </ol>
          </details>
        </div>
      )}
    </div>
  );
}
